import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { environment } from 'src/environments/environment';
import { MetricasService } from '../../service/metricas.service';
import { FiltroAcoesComponent } from '../filtros-metricas/filtro-acoes/filtro-acoes.component';
import { FiltroPeriodoComponent } from '../filtros-metricas/filtro-periodo/filtro-periodo.component';
import { FiltroResponsavelComponent } from '../filtros-metricas/filtro-responsavel/filtro-responsavel.component';
import { FiltroTipoObraComponent } from './../filtros-metricas/filtro-tipo-obra/filtro-tipo-obra.component';

import {
  ApexChart,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTooltip,
  ChartComponent,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { finalize } from 'rxjs';
import { TabelaPaginacaoComponent } from 'src/app/shared/components/tabela-paginacao/tabela-paginacao.component';
import { Column } from '../../interface/colunas-tabelas.interface';
import {
  PaginationMetricas,
  TabelasObrasFase,
} from '../../interface/tabela-obras-fase.interface';
import { DropdownService } from '../../service/dropdown-metricas.service';
import { PeriodoService } from '../filtros-metricas/filtro-periodo/service/filtro-periodo.service';
import { OBRAS_POR_TAREFAS_COLUMNS } from './obras-por-tarefas.columns';
import { ModalAguardandoDownloadComponent } from 'src/app/shared/components/modal-aguardando-download/modal-aguardando-download.component';

(window as any).ApexCharts = ApexCharts;

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  responsive: ApexResponsive[];
  colors: string[];
  plotOptions: ApexPlotOptions;
  tooltip: ApexTooltip;
  legend?: any;
};

export interface Action {
  actionID: number;
  action: string;
  workQuantity: number;
}

@Component({
  selector: 'app-obras-por-tarefas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    FiltroResponsavelComponent,
    FiltroPeriodoComponent,
    FiltroTipoObraComponent,
    FiltroAcoesComponent,
    TabelaPaginacaoComponent,
    ModalAguardandoDownloadComponent,
  ],
  templateUrl: './obras-por-tarefas.component.html',
  styleUrls: ['./obras-por-tarefas.component.scss'],
  providers: [MetricasService],
})
export class ObrasPorTarefasComponent implements OnInit {
  columns: Column[] = OBRAS_POR_TAREFAS_COLUMNS;
  processandoAcao: boolean = false;
  mensagemProcessando: string = 'Aguardando download...';

  trackByFn = (index: number, item: any) => item.id;

  BASE_IMG_URL: string = environment.BASE_IMG_URL;

  arrObrasPorTarefa: Action[] = [];
  todasAsTarefas: Action[] = [];

  acoes: any[] = [];
  selectedAcoes: any[] = [];

  intervaloSelecionado: Date[] | null = null;
  selectedFaseObra: number | 'Todas' = 'Todas';

  get desabilitarExportar(): boolean {
    return this.arrObrasPorTarefa.length === 0 && this.tableData.length === 0;
  }

  @Input() selectedPeriodo: Date[] | null = null;
  @Input() obraIds: number[] = [];
  @Input() responsavel: any[] = [];
  @Input() selectedUsuario: string = 'Todos';

  @Output() atualizandoMetricasChange = new EventEmitter<boolean>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() obraSelecionada = new EventEmitter<{
    workId: number;
    obraIds: number[];
    moduleId?: number;
    workType?: string;
    workTypesPorObra?: Record<number, number>;
  }>();
  @Output() avancar = new EventEmitter<void>();
  @Output() voltarObra = new EventEmitter<void>();
  @Output() companyNameClick = new EventEmitter<{
    companyId?: number;
    companyName?: string;
    workType?: string;
  }>();
  @Output() periodoChange = new EventEmitter<{
    periodBegin: string;
    periodEnd: string;
  } | null>();
  @ViewChild('chart') chart?: ChartComponent;

  config: Partial<BsDaterangepickerConfig> = {
    containerClass: 'bs-datepicker',
    rangeInputFormat: 'DD/MM/YYYY',
    showWeekNumbers: false,
    selectFromOtherMonth: true,
    isAnimated: true,
    displayMonths: 1,
  };

  currentPage: number = 1;
  pageNumber: number = 1;
  pageSize: number = 50;

  idObraSelecionada: number | null = null;
  indiceObraAtual: number = 0;
  exibirFichaDaObra: boolean = false;

  chartOptions!: ChartOptions;
  viewMode: 'chart' | 'table' = 'chart';
  chartReady = false;

  tiposSelecionados: any;

  filtroFaseObraSelecionados: number[] = [];

  mostrarFaseObra = false;

  tableData: TabelasObrasFase[] = [];
  pagination: PaginationMetricas = {
    pageNext: 2,
    pagePrev: 0,
    recordsCount: 0,
    pageFirst: 1,
    pageLast: 1,
    pageActive: true,
    page: 1,
    pageSize: 10,
    shortcuts: 0,
    hasValue: false,
    shortcutPages: [],
    records: [],
  };
  private periodoService = inject(PeriodoService);

  private sincronizarPeriodoComFiltro = effect(() => {
    const periodo = this.periodoService.periodoSelecionado()?.();

    this.listWorkByTask(periodo ?? undefined);
  });

  periodoSelecionado: Date[] | null = null;

  constructor(
    private _metricasService: MetricasService,
    private dropdownService: DropdownService
  ) {}

  ngOnInit(): void {
    this.chartOptions = {
      series: [],
      chart: {
        type: 'pie',
      },
      labels: [],
      responsive: [],
      colors: [],
      plotOptions: {},
      tooltip: {},
    };

    this.dropdownService.getActiveDropdown().subscribe((active) => {
      if (active !== 'faseObra') {
        this.mostrarFaseObra = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['responsavel'] && !changes['responsavel'].firstChange) {
      const responsaveis = changes['responsavel'].currentValue;

      if (
        Array.isArray(responsaveis) &&
        responsaveis.length > 0 &&
        responsaveis[0]?.userLevelId
      ) {
        const p = this.periodObjFromSelected();

        this.listWorkByTask(p);
        this.listWorkByTaskTable(p);
      } else {
      }
    }

    if (changes['selectedPeriodo'] && !changes['selectedPeriodo'].firstChange) {
      const periodo = this.periodObjFromSelected();
      this.listWorkByTask(periodo);
      this.listWorkByTaskTable(periodo);
    }
  }

  listResponsaveis(): void {
    if (this.responsavel.length === 0) {
      this._metricasService.listResponsible().subscribe(
        (response) => {
          this.responsavel = response.data.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        },
        (error) => {
          console.error('Erro ao buscar responsáveis:', error);
        }
      );
    }
  }

  get totalPages(): number {
    return this.totalPagesValue;
  }

  totalPagesValue: number = 0;

  onPageChange(page: number): void {
    this.pagination.page = page;
    this.listWorkByTaskTable();
  }

  listWorkByTaskTable(periodo?: {
    periodBegin: string;
    periodEnd: string;
  }): void {
    this.atualizandoMetricasChange.emit(true);

    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      return;
    }
    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }

    const requestBody = {
      actionID: this.selectedAcoes?.map((a) => a.id).join(',') ?? '',
      categoryID: this.tiposSelecionados?.join(',') ?? '',
      responsibleID: responsibleID,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
      pagination: {
        page: this.currentPage,
        pageSize: this.pageSize,
        shortcuts: 10,
        ordenation: '',
      },
    };
    this._metricasService
      .listWorkTaskTable(requestBody)
      .pipe(finalize(() => this.atualizandoMetricasChange.emit(false)))
      .subscribe(
        (response) => {
          this.atualizandoMetricasChange.emit(false);
          if (response && response.data && response.data.records) {
            this.tableData = response.data.records.map((item: any) => ({
              responsibleId: item.responsibleId,
              responsibleName: item.responsibleName,
              stateId: item.stateId,
              stateName: ` ${item.cityName} - ${item.stateName} `,
              cityId: item.cityId,
              cityName: item.cityName,
              workId: item.workId,
              companyId: item.companyId,
              companyName: item.companyName,
              workName: item.workName,
              workTypeId: item.workTypeId,
              workType: item.workType,
              marketPhaseId: item.marketPhaseId,
              marketPhase: item.marketPhase,
              lastAction: item.lastAction,
              lastActionDate: formatDate(
                item.lastActionDate,
                'dd/MM/yyyy',
                'en-US'
              ),
              lastNote: item.lastNote,
              lastPotential: item.lastPotential,
              viewStatus: item.viewStatus,
              assignedDate: formatDate(
                item.assignedDate,
                'dd/MM/yyyy',
                'en-US'
              ),
            }));

            this.obraIds = this.tableData.map((obra) => obra.workId);
            
            const totalRegistros =
              response.data.totalRecords ?? response.data.recordsCount ?? 0;

            this.totalPagesValue = Math.ceil(totalRegistros / this.pageSize);

            this.pagination = {
              ...this.pagination,
              page: this.currentPage,
              recordsCount: totalRegistros,
              pageFirst: 1,
              pageLast: this.totalPagesValue,
              pageNext:
                this.currentPage < this.totalPagesValue
                  ? this.currentPage + 1
                  : null,
              pagePrev: this.currentPage > 1 ? this.currentPage - 1 : null,
              hasValue: this.tableData.length > 0,
              records: this.tableData,
            };
          } else {
            console.error('response.data.records não está definido:', response);
            this.tableData = [];
          }
        },
        (error) => {
          this.atualizandoMetricasChange.emit(false);
          console.error('Erro ao buscar fases de obra:', error);
        }
      );
  }

  mudarPagina(novaPagina: number): void {
    this.currentPage = novaPagina;
    this.listWorkByTaskTable();
  }

  listWorkByTask(periodo?: { periodBegin: string; periodEnd: string }): void {
    this.atualizandoMetricasChange.emit(true);
    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      return;
    }

    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }

    const requestBody = {
      actionID: this.selectedAcoes?.map((a) => a.id).join(',') ?? '',
      categoryID: this.tiposSelecionados?.join(',') ?? '',
      responsibleID: responsibleID,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
    };
    this.atualizandoMetricasChange.emit(true);
    this._metricasService
      .listWorkTask(requestBody)
      .pipe(finalize(() => this.atualizandoMetricasChange.emit(false)))
      .subscribe(
        (response) => {
          this.atualizandoMetricasChange.emit(false);
          if (response && response.data && response.data.length > 0) {
            if (this.todasAsTarefas.length === 0) {
              this.todasAsTarefas = [...response.data];
            }

            this.arrObrasPorTarefa = response.data;
            
            const series = response.data.map(
              (item: Action) => item.workQuantity
            );
            const labels = response.data.map((item: Action) => item.action);

            this.chartOptions = {
              series: series,
              chart: {
                type: 'pie',
                height: 260,
              },
              labels: labels,
              colors: [
                '#eb5e28',
                '#6b6864',
                '#b4ada1',
                '#f2efe5',
                '#252422',
                '#e67d45',
                '#4a4845',
                '#252422',
                '#eb5e28',
                '#6b6864',
                '#b4ada1',
              ],
              plotOptions: {
                pie: {
                  donut: {
                    size: '40%',
                  },
                },
              },
              tooltip: {
                y: {
                  formatter: (val: number) => `${val} obras`,
                },
              },
              legend: {
                show: false,
                position: 'right',
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: 'Hiragino-W3", sans-serif',
                labels: {
                  colors: [
                    '#586572',
                    '#586572',
                    '#586572',
                    '#586572',
                    '#586572',
                  ],
                  useSeriesColors: false,
                },
                markers: {
                  radius: 12,
                  offsetX: -5,
                },
                itemMargin: {
                  vertical: 10,
                  horizontal: 20,
                },
                formatter: (seriesName: string, opts) => {
                  const val = opts.w.globals.series[opts.seriesIndex];
                  return `
                <div style="display: flex; align-items: center; justify-content: space-between;">
                <div>
                  <span style="padding-right: 12px;">${seriesName}</span>
               
                </div>
                <div>
                 <strong>${val}</strong>
                </div>
              `;
                },
              },
              responsive: [
                {
                  breakpoint: 768,
                  options: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                },
              ],
            };

            this.chartReady = true;
          } else {
            this.arrObrasPorTarefa = [];
            this.chartReady = false;
          }
        },
        (error) => {
          this.chartReady = false;
          console.error('Erro ao buscar fases de obra:', error);
        }
      );
  }

  private parseYmdToLocal(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  private formatDateToBackendFormat(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  onPeriodoSelecionadoChange(
    periodo: { periodBegin: string; periodEnd: string } | null
  ) {
    if (periodo && periodo.periodBegin && periodo.periodEnd) {
      this.selectedPeriodo = [
        this.parseYmdToLocal(periodo.periodBegin),
        this.parseYmdToLocal(periodo.periodEnd),
      ];
    } else {
      this.selectedPeriodo = null;
    }
    this.listWorkByTask(periodo ?? undefined);
    this.listWorkByTaskTable(periodo ?? undefined);
  }

  onAcoesSelecionadasChange() {
    const periodo = this.periodObjFromSelected();
    this.listWorkByTask(periodo);
    this.listWorkByTaskTable(periodo);
  }

  toggleView(mode: 'chart' | 'table') {
    this.viewMode = mode;
  }

  toggleFaseObra() {
    const isOpening = !this.mostrarFaseObra;
    this.dropdownService.setActiveDropdown(isOpening ? 'faseObra' : null);
    this.mostrarFaseObra = isOpening;
  }

  private periodObjFromSelected():
    | { periodBegin: string; periodEnd: string }
    | undefined {
    if (this.selectedPeriodo?.length === 2) {
      return {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    return undefined;
  }

  private formatYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  exportExcel() {
    this.mensagemProcessando = 'Aguardando download do Excel...';
    this.processandoAcao = true;
    const filtros = this.capturarFiltrosAtuais();
    this._metricasService.downloadExcelWorkTask(filtros).subscribe(
      (response) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'obras-por-tarefas.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.processandoAcao = false;
      },
      (error) => {
        this.processandoAcao = false;
        console.error('Erro ao exportar Excel:', error);
      }
    );
  }

  capturarFiltrosAtuais(): any {
    const filtros: any = {};

    let period: { periodBegin: string; periodEnd: string } | null = null;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      period = {
        periodBegin: this.formatDateToBackendFormat(this.selectedPeriodo[0]),
        periodEnd: this.formatDateToBackendFormat(this.selectedPeriodo[1]),
      };
    }

    if (period) {
      filtros.period = period;
    }

    let responsibleId: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleId = Number(this.selectedUsuario) || 0;
    }

    if (responsibleId !== 0) {
      filtros.responsible = {
        responsible: responsibleId
      };
    }

    const acoesIds = this.selectedAcoes || [];
    if (acoesIds.length > 0) {
      filtros.action = {
        action: acoesIds
      };
    }

    const tiposObraIds = this.tiposSelecionados || [];
    if (tiposObraIds.length > 0) {
      filtros.type = {
        ids: tiposObraIds
      };
    }

    return filtros;
  }

  onTiposSelecionadosChange(tipos: number[]): void {
    this.tiposSelecionados = tipos;
    let periodo: { periodBegin: string; periodEnd: string } | undefined =
      undefined;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      periodo = {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    this.listWorkByTask(periodo ?? undefined);
    this.listWorkByTaskTable(periodo ?? undefined);
  }

  limparFasesObra(): void {
    this.filtroFaseObraSelecionados = [];
    this.mostrarFaseObra = false;
    const periodo = this.periodObjFromSelected();
    this.listWorkByTask();
    this.listWorkByTaskTable();
  }

  selecionarFase(fase: string) {
    this.filtroFaseObraSelecionados.push(Number(fase));
    const periodo = this.periodObjFromSelected();
    this.listWorkByTask(periodo);
    this.listWorkByTaskTable(periodo);
  }
  onFaseChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;

    if (valor === 'Todas') {
      this.filtroFaseObraSelecionados = [];
    } else {
      const faseSelecionada = Number(valor);
      this.filtroFaseObraSelecionados = [faseSelecionada];
    }

    const periodo =
      this.selectedPeriodo?.length === 2
        ? {
            periodBegin: this.formatYmd(this.selectedPeriodo[0]),
            periodEnd: this.formatYmd(this.selectedPeriodo[1]),
          }
        : { periodBegin: '', periodEnd: '' };
    this.listWorkByTask(periodo);
    this.listWorkByTaskTable(periodo);

    this.mostrarFaseObra = false;
    this.atualizandoMetricasChange.emit(false);
  }

  onResponsavelIdChange(responsavelId: number): void {
    const periodo = this.periodObjFromSelected();
    this.listWorkByTask(periodo);
    this.listWorkByTaskTable(periodo);
  }

  onWorkNameSelected(workId: number): void {
    const obra = this.tableData.find((data) => data.workId === workId);
    const workTypesPorObra: Record<number, number> = {};
    this.tableData.forEach((data) => {
      if (data.workTypeId != null) workTypesPorObra[data.workId] = data.workTypeId;
    });
    this.obraSelecionada.emit({
      workId,
      obraIds: this.tableData.map((data) => data.workId),
      ...(obra?.workTypeId != null ? { moduleId: obra.workTypeId } : {}),
      ...(obra?.workType ? { workType: obra.workType } : {}),
      workTypesPorObra,
    });
  }

  onCompanyNameClick(row: any): void {
    this.companyNameClick.emit({
      companyId: row.companyId,
      companyName: row.companyName,
      workType: row.workType,
    });
  }
  
  listActions(): void {
    this._metricasService.listActions().subscribe(
      (response) => {
        this.acoes = response;
      },

      (error) => {
        console.error('Erro ao buscar ações:', error);
      }
    );
  }

  onResponsaveisRecebidos(resps) {
    if (!Array.isArray(resps)) {
      console.error('Responsáveis recebidos não são um array:', resps);
      return;
    }
    if (resps.length === 0) {
      console.warn('Array de responsáveis está vazio.');
    }

    if (resps.length > 0 && resps[0].id === undefined) {
      console.error('Responsável não possui propriedade id:', resps[0]);
    }
  }
}
